/**
 * kan-118 — pure helpers for the Reference Form (AddReferenceModal).
 *
 * The form no longer hard-codes nine textareas: it renders the candidate's
 * CURRENT AI Reference Taker question set (merged server-side with any stored
 * answers — see the API's reference-form-merge.ts), so customising the set in
 * the gear modal changes the form too. Kept pure so the mapping rules are
 * unit-tested without mounting the modal.
 */

/** fieldKey enum KEY the API returns for the "relationship" question. */
export const RELATIONSHIP_FIELD_KEY = 'RELATIONSHIP_TO_CANDIDATE';

export type ReferenceFormQuestion = {
  templateId: string | null;
  label: string;
  prompt: string;
  fieldKey: string | null;
  answer: string;
  sortOrder: number;
  inCurrentSet: boolean;
  source: string;
};

export type ReferenceFormContact = {
  referenceName?: string | null;
  title?: string | null;
  company?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  relationshipToCandidate?: string | null;
  status?: string | null;
};

export type StatusOption = { value: string; label: string };

export type ReferenceFormValues = {
  referenceName: string;
  title: string;
  company: string;
  phoneNumber: string;
  email: string;
  relationshipToCandidate: string;
  status: StatusOption | null;
  // Keyed by the question's index in the loaded list (answers.<index>) so
  // current-set and orphan questions share one array in server order.
  answers: string[];
};

/**
 * True when the loaded set already asks the relationship question — then its
 * textarea IS the relationship field and the separate contact input is hidden
 * (the API mirrors that answer onto Reference.relationshipToCandidate).
 */
export const hasRelationshipQuestion = (
  questions: ReferenceFormQuestion[] | null | undefined,
): boolean => (questions ?? []).some((q) => q?.fieldKey === RELATIONSHIP_FIELD_KEY);

/**
 * Splits the list into the current set and orphans (answers whose question
 * left the set), keeping each question's index into the full list so the
 * react-hook-form name answers.<index> stays stable across both groups.
 */
export const splitQuestions = (
  questions: ReferenceFormQuestion[] | null | undefined,
): {
  current: { question: ReferenceFormQuestion; index: number }[];
  orphans: { question: ReferenceFormQuestion; index: number }[];
} => {
  const current: { question: ReferenceFormQuestion; index: number }[] = [];
  const orphans: { question: ReferenceFormQuestion; index: number }[] = [];
  (questions ?? []).forEach((question, index) => {
    (question.inCurrentSet ? current : orphans).push({ question, index });
  });
  return { current, orphans };
};

export const buildDefaultValues = (
  referenceData: ReferenceFormContact | null | undefined,
  questions: ReferenceFormQuestion[] | null | undefined,
): ReferenceFormValues => {
  const status = referenceData?.status;
  return {
    referenceName: referenceData?.referenceName ?? '',
    title: referenceData?.title ?? '',
    company: referenceData?.company ?? '',
    phoneNumber: referenceData?.phoneNumber ?? '',
    email: referenceData?.email ?? '',
    relationshipToCandidate: referenceData?.relationshipToCandidate ?? '',
    status: status ? { value: status, label: status } : null,
    answers: (questions ?? []).map((q) => q.answer ?? ''),
  };
};

const trimOrNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

/**
 * Builds SaveReferenceFormInput. Every rendered question is sent (blank means
 * "delete this answer" server-side); the API re-merges and matches by
 * templateId / normalized label, so the label sent is the one it returned.
 */
export const toSaveInput = (
  values: ReferenceFormValues,
  questions: ReferenceFormQuestion[] | null | undefined,
  ctx: { candidateId: string; referenceId?: string | null },
): Record<string, unknown> => {
  const list = questions ?? [];
  const statusRaw: any = values?.status;
  const status: string | null =
    (typeof statusRaw === 'string' ? statusRaw : statusRaw?.value) || null;

  const input: Record<string, unknown> = {
    referenceId: ctx.referenceId || null,
    candidateId: ctx.candidateId,
    referenceName: (values.referenceName ?? '').trim(),
    title: trimOrNull(values.title),
    company: trimOrNull(values.company),
    phoneNumber: trimOrNull(values.phoneNumber),
    email: trimOrNull(values.email),
    status,
    answers: list.map((q, i) => ({
      templateId: q.templateId ?? null,
      label: q.label,
      answer: values.answers?.[i] ?? '',
    })),
  };
  // When the set asks the relationship question, that answer is mirrored onto
  // the column server-side; sending the contact input too would be ambiguous.
  if (!hasRelationshipQuestion(list)) {
    input.relationshipToCandidate = trimOrNull(values.relationshipToCandidate);
  }
  return input;
};
